/**
 * Advanced Position Evaluation System
 *
 * Sophisticated evaluation function for Minimax AI that considers:
 * - Position value and stability
 * - Mobility and potential moves
 * - Corner and edge control
 * - Game phase awareness
 * - Parity and timing
 */

import {
  BoardPosition,
  PlayerColor,
  positionToCoords,
  isCorner,
  isEdge,
  isXSquare,
  isCSquare,
  BOARD_SIZE,
} from '@/types/game-types';
import type { GameState } from '@/core/models/GameState';

/**
 * Comprehensive position evaluation weights
 */
const EVALUATION_WEIGHTS = {
  POSITION_VALUE: 1.0,
  MOBILITY: 2.0,
  STABILITY: 3.0,
  CORNER_CONTROL: 5.0,
  EDGE_CONTROL: 1.5,
  POTENTIAL_MOBILITY: 1.0,
  PARITY: 0.5,
  ENDGAME_MATERIAL: 10.0,
} as const;

/**
 * Static position values for advanced evaluation
 */
const ADVANCED_POSITION_VALUES = [
  100, -20,  10,   5,   5,  10, -20, 100,
  -20, -50,  -2,  -2,  -2,  -2, -50, -20,
   10,  -2,  16,   2,   2,  16,  -2,  10,
    5,  -2,   2,   1,   1,   2,  -2,   5,
    5,  -2,   2,   1,   1,   2,  -2,   5,
   10,  -2,  16,   2,   2,  16,  -2,  10,
  -20, -50,  -2,  -2,  -2,  -2, -50, -20,
  100, -20,  10,   5,   5,  10, -20, 100,
] as const;

/**
 * Stability values for each position
 */
const STABILITY_VALUES = [
  4, 0, 3, 2, 2, 3, 0, 4,
  0, 0, 1, 1, 1, 1, 0, 0,
  3, 1, 2, 1, 1, 2, 1, 3,
  2, 1, 1, 1, 1, 1, 1, 2,
  2, 1, 1, 1, 1, 1, 1, 2,
  3, 1, 2, 1, 1, 2, 1, 3,
  0, 0, 1, 1, 1, 1, 0, 0,
  4, 0, 3, 2, 2, 3, 0, 4,
] as const;

/**
 * Result of position evaluation
 */
export interface EvaluationResult {
  readonly totalScore: number;
  readonly breakdown: {
    readonly positionValue: number;
    readonly mobility: number;
    readonly stability: number;
    readonly cornerControl: number;
    readonly edgeControl: number;
    readonly potentialMobility: number;
    readonly parity: number;
    readonly material: number;
  };
}

/**
 * Advanced position evaluator for AI strategies
 */
export class AdvancedEvaluator {
  /**
   * Evaluate position for a specific player
   */
  static evaluatePosition(gameState: GameState, player: PlayerColor): EvaluationResult {
    const board = gameState.board as Uint8Array;
    const opponent = player === PlayerColor.BLACK ? PlayerColor.WHITE : PlayerColor.BLACK;

    // Calculate individual evaluation components
    const positionValue = this.evaluatePositionValue(board, player, opponent);
    const mobility = this.evaluateMobility(gameState, player, opponent);
    const stability = this.evaluateStability(board, player, opponent);
    const cornerControl = this.evaluateCornerControl(board, player, opponent);
    const edgeControl = this.evaluateEdgeControl(board, player, opponent);
    const potentialMobility = this.evaluatePotentialMobility(board, player, opponent);
    const parity = this.evaluateParity(board, player, opponent);
    const material = this.evaluateMaterial(board, player, opponent);

    // Calculate weighted total score
    const totalScore =
      positionValue * EVALUATION_WEIGHTS.POSITION_VALUE +
      mobility * EVALUATION_WEIGHTS.MOBILITY +
      stability * EVALUATION_WEIGHTS.STABILITY +
      cornerControl * EVALUATION_WEIGHTS.CORNER_CONTROL +
      edgeControl * EVALUATION_WEIGHTS.EDGE_CONTROL +
      potentialMobility * EVALUATION_WEIGHTS.POTENTIAL_MOBILITY +
      parity * EVALUATION_WEIGHTS.PARITY +
      material * EVALUATION_WEIGHTS.ENDGAME_MATERIAL;

    return {
      totalScore,
      breakdown: {
        positionValue,
        mobility,
        stability,
        cornerControl,
        edgeControl,
        potentialMobility,
        parity,
        material,
      },
    };
  }

  /**
   * Evaluate position values of pieces on board
   */
  private static evaluatePositionValue(
    board: Uint8Array,
    player: PlayerColor,
    opponent: PlayerColor,
  ): number {
    let score = 0;

    for (let position = 0; position < 64; position++) {
      const piece = board[position];
      if (piece === player) {
        score += ADVANCED_POSITION_VALUES[position] ?? 0;
      } else if (piece === opponent) {
        score -= ADVANCED_POSITION_VALUES[position] ?? 0;
      }
    }

    return score;
  }

  /**
   * Evaluate current mobility (number of valid moves)
   */
  private static evaluateMobility(
    gameState: GameState,
    player: PlayerColor,
    opponent: PlayerColor,
  ): number {
    const playerMobility = gameState.currentPlayer === player ? gameState.validMoves.size : 0;
    const opponentMobility = gameState.currentPlayer === opponent ? gameState.validMoves.size : 0;

    // More mobility is better, but avoid division by zero
    if (playerMobility + opponentMobility === 0) return 0;

    return ((playerMobility - opponentMobility) / (playerMobility + opponentMobility)) * 100;
  }

  /**
   * Evaluate stability of pieces (how hard they are to flip)
   */
  private static evaluateStability(
    board: Uint8Array,
    player: PlayerColor,
    opponent: PlayerColor,
  ): number {
    let playerStability = 0;
    let opponentStability = 0;

    for (let position = 0; position < 64; position++) {
      const piece = board[position];
      const stabilityValue = this.calculatePieceStability(board, position);

      if (piece === player) {
        playerStability += stabilityValue;
      } else if (piece === opponent) {
        opponentStability += stabilityValue;
      }
    }

    return playerStability - opponentStability;
  }

  /**
   * Calculate stability of a single piece
   */
  private static calculatePieceStability(board: Uint8Array, position: BoardPosition): number {
    const piece = board[position];
    if (piece === PlayerColor.EMPTY || piece === undefined) return 0;

    let stability = STABILITY_VALUES[position] ?? 0;

    // Corner pieces are completely stable
    if (isCorner(position)) {
      return stability + 10;
    }

    // Check stability in all 8 directions
    const directions = [-9, -8, -7, -1, 1, 7, 8, 9];
    let stableDirections = 0;

    for (const dir of directions) {
      if (this.isDirectionStable(board, position, dir, piece)) {
        stableDirections++;
      }
    }

    return stability + stableDirections;
  }

  /**
   * Check if a piece is stable in a specific direction
   */
  private static isDirectionStable(
    board: Uint8Array,
    position: BoardPosition,
    direction: number,
    piece: PlayerColor,
  ): boolean {
    const pos1 = position + direction;
    const pos2 = position - direction;

    // Check bounds
    if (pos1 < 0 || pos1 >= 64 || pos2 < 0 || pos2 >= 64) return true;

    // Both directions should have same color or edge
    return board[pos1] === piece || board[pos2] === piece;
  }

  /**
   * Evaluate corner control
   */
  private static evaluateCornerControl(
    board: Uint8Array,
    player: PlayerColor,
    opponent: PlayerColor,
  ): number {
    const corners = [0, 7, 56, 63];
    let playerCorners = 0;
    let opponentCorners = 0;

    for (const corner of corners) {
      if (board[corner] === player) {
        playerCorners++;
      } else if (board[corner] === opponent) {
        opponentCorners++;
      }
    }

    return (playerCorners - opponentCorners) * 25;
  }

  /**
   * Evaluate edge control
   */
  private static evaluateEdgeControl(
    board: Uint8Array,
    player: PlayerColor,
    opponent: PlayerColor,
  ): number {
    let playerEdges = 0;
    let opponentEdges = 0;

    for (let position = 0; position < 64; position++) {
      if (isEdge(position) && !isCorner(position)) {
        if (board[position] === player) {
          playerEdges++;
        } else if (board[position] === opponent) {
          opponentEdges++;
        }
      }
    }

    return playerEdges - opponentEdges;
  }

  /**
   * Evaluate potential mobility (empty squares adjacent to opponent pieces)
   */
  private static evaluatePotentialMobility(
    board: Uint8Array,
    player: PlayerColor,
    opponent: PlayerColor,
  ): number {
    let playerPotential = 0;
    let opponentPotential = 0;

    for (let position = 0; position < 64; position++) {
      if (board[position] === PlayerColor.EMPTY) {
        const adjacentToPlayer = this.isAdjacentToColor(board, position, player);
        const adjacentToOpponent = this.isAdjacentToColor(board, position, opponent);

        if (adjacentToPlayer) playerPotential++;
        if (adjacentToOpponent) opponentPotential++;
      }
    }

    return playerPotential - opponentPotential;
  }

  /**
   * Check if a position is adjacent to pieces of a specific color
   */
  private static isAdjacentToColor(
    board: Uint8Array,
    position: BoardPosition,
    color: PlayerColor,
  ): boolean {
    const directions = [-9, -8, -7, -1, 1, 7, 8, 9];

    for (const dir of directions) {
      const adjacent = position + dir;
      if (adjacent >= 0 && adjacent < 64 && board[adjacent] === color) {
        return true;
      }
    }

    return false;
  }

  /**
   * Evaluate parity (who gets the last move)
   */
  private static evaluateParity(
    board: Uint8Array,
    player: PlayerColor,
    opponent: PlayerColor,
  ): number {
    let emptySquares = 0;

    for (let i = 0; i < 64; i++) {
      if (board[i] === PlayerColor.EMPTY) {
        emptySquares++;
      }
    }

    // If odd number of empty squares, current player has advantage
    return emptySquares % 2 === 1 ? 1 : -1;
  }

  /**
   * Evaluate material balance (piece count) - important in endgame
   */
  private static evaluateMaterial(
    board: Uint8Array,
    player: PlayerColor,
    opponent: PlayerColor,
  ): number {
    let playerPieces = 0;
    let opponentPieces = 0;
    let emptySquares = 0;

    for (let i = 0; i < 64; i++) {
      const piece = board[i];
      if (piece === player) {
        playerPieces++;
      } else if (piece === opponent) {
        opponentPieces++;
      } else {
        emptySquares++;
      }
    }

    // Material evaluation is more important in endgame
    const endgameWeight = emptySquares < 16 ? 2.0 : 0.1;

    return (playerPieces - opponentPieces) * endgameWeight;
  }

  /**
   * Quick evaluation for move ordering (faster version)
   */
  static quickEvaluate(
    board: Uint8Array,
    position: BoardPosition,
    player: PlayerColor,
    flippedPositions: BoardPosition[],
  ): number {
    let score = 0;

    // Position value
    score += ADVANCED_POSITION_VALUES[position] ?? 0;

    // Flipped pieces count
    score += flippedPositions.length * 5;

    // Corner bonus
    if (isCorner(position)) {
      score += 50;
    }

    // Avoid X-squares early
    if (isXSquare(position)) {
      score -= 25;
    }

    return score;
  }

  /**
   * Game phase detection
   */
  static getGamePhase(board: Uint8Array): 'opening' | 'midgame' | 'endgame' {
    let emptySquares = 0;

    for (let i = 0; i < 64; i++) {
      if (board[i] === PlayerColor.EMPTY) {
        emptySquares++;
      }
    }

    if (emptySquares > 40) return 'opening';
    if (emptySquares > 16) return 'midgame';
    return 'endgame';
  }
}

/**
 * Export convenience functions
 */
export const evaluatePosition = AdvancedEvaluator.evaluatePosition;
export const quickEvaluate = AdvancedEvaluator.quickEvaluate;
export const getGamePhase = AdvancedEvaluator.getGamePhase;